<template>
  <section class="section">
    <h2 class="title">
      {{ $t('route.dataInputs') }}
      <div class="level">
        <div class="level-left subtitle">
          {{ $t('subtitle.dataInputs') }}
        </div>
        <div class="level-right">
          <div class="field is-grouped">
            <div class="control">
              <button
                class="button is-primary"
                @click="showFilter = true"
                :class="{ 'is-info': hasActiveFilters }"
              >
                <octicon :icon="search" />
                <span>{{ $t('users.filter.title') }}</span>
                <span v-if="hasActiveFilters" class="tag is-light ml-1">{{ activeFilterCount }}</span>
              </button>
            </div>
            <div class="control">
              <button class="button is-primary" @click.prevent="create">
                <octicon :icon="plus" /><span>{{ $t('newDataInput') }}</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </h2>
    <box>
      <data-table
        :data="dataInputs"
        :columns="columns"
        :searchable="true"
        :search-placeholder="$t('dataInputs.searchPlaceholder')"
        :striped="true"
        :hoverable="true"
        :show-pagination="true"
        :items-per-page-options="[10, 15, 30, 50]"
        :default-items-per-page="10"
        default-sort-order="desc"
        :loading="loading"
        :empty-message="$t('datatable.noData')"
        :server-side="true"
        :total-items="totalItems"
        @search="handleSearch"
        @sort="handleSort"
        @page-change="handlePageChange"
        @page-size-change="handlePageSizeChange"
      >
        <!-- Custom cell for STT (Serial Number) -->
        <template #cell-stt="{ index }">
          {{ index + 1 }}
        </template>

        <!-- Custom cell for Name -->
        <template #cell-source_rename="{ value }">
          <div class="truncate-cell" :title="value" v-html="highlightSearchText(value)"></div>
        </template>

        <!-- Custom cell for Description -->
        <template #cell-description="{ value }">
          <div class="truncate-cell" :title="value" v-html="highlightSearchText(value)"></div>
        </template>

        <!-- Custom cell for Source Type -->
        <template #cell-source_type="{ value }">
          <div class="truncate-cell" v-if="sourceTypeNames[value]" :title="sourceTypeNames[value].name">
            {{ sourceTypeNames[value].name }}
          </div>
          <div v-else>-</div>
        </template>

        <!-- Custom cell for Index -->
        <template #cell-index="{ value }">
          <div class="truncate-cell" v-if="indexNames[value]" :title="indexNames[value].name">
            {{ indexNames[value].name }}
          </div>
          <div v-else>-</div>
        </template>

        <!-- Custom cell for Status -->
        <template #cell-enabled="{ value }">
          <span v-if="value" class="tag is-success is-light">{{ $t('active') }}</span>
          <span v-else class="tag is-warning is-light">{{ $t('inactive') }}</span>
        </template>

        <!-- Custom cell for SSL -->
        <template #cell-ssl="{ row }">
          <span v-if="row.ssl" class="tag is-success is-light">{{ $t('enabled') }}</span>
          <span v-else class="tag is-danger is-light">{{ $t('disabled') }}</span>
        </template>

        <!-- Custom cell for Protocol -->
        <template #cell-protocol="{ row }">
          <span v-if="row.protocol === 'http'" class="tag is-uppercase is-warning is-light">{{ row.protocol }}</span>
          <span v-else-if="row.protocol === 'tcp'" class="tag is-uppercase is-success is-light">{{ row.protocol }}</span>
          <span v-else-if="row.protocol === 'udp'" class="tag is-uppercase is-info is-light">{{ row.protocol }}</span>
          <span v-else>{{ row.protocol }}</span>
        </template>

        <!-- Custom cell for Port -->
        <template #cell-port="{ row }">
          <span v-if="row.protocol === 'tcp' || row.protocol === 'udp'" class="tag is-primary is-light">
            {{ row.port }}
          </span>
          <span v-else>-</span>
        </template>

        <!-- Custom cell for Created At -->
        <template #cell-created_at="{ value }">
          <span>{{ formatDateTime(value) }}</span>
        </template>

        <!-- Custom cell for Actions -->
        <template #cell-actions="{ row }">
          <div class="has-text-centered">
            <button class="button is-text" @click.prevent="edit(row)" :title="$t('edit')">
              <octicon :icon="pencil" />
            </button>
            <button
              class="button is-text"
              @click.prevent="toggleActivation(row)"
              :title="row.enabled ? $t('deactivate') : $t('activate')"
              :class="[
                row.enabled ? 'has-text-warning' : 'has-text-success',
                { 'is-loading': toggleLoading[row.id] }
              ]"
              :disabled="toggleLoading[row.id]"
            >
              <octicon :icon="row.enabled ? circleSlash : play" />
            </button>
            <button class="button is-text" @click.prevent="destroy(row)" :title="$t('delete')">
              <octicon :icon="x" />
            </button>
          </div>
        </template>
      </data-table>
    </box>
    <modal v-if="item" :show.sync="modal" class="dark-modal">
      <div class="modal-box">
        <div class="modal-box-header">
          <h2 class="modal-title">
            <template v-if="item.id">
              {{ $t('edit') }} {{ $t('route.dataInputs') }} - {{ item.source_rename }}
            </template>
            <template v-else>
              {{ $t('newDataInput') }}
            </template>
          </h2>
        </div>

        <form @submit.prevent="submit">
          <div class="field">
            <label class="label">
              {{ $t('name') }} *
              <popover placement="top" width="300px">
                <span slot="trigger" class="icon is-small has-text-info" style="cursor: help;">
                  <octicon :icon="info" />
                </span>
                <div class="has-text-left is-size-7">
                  {{ $t('dataInputs.tooltips.name') }}
                </div>
              </popover>
              <span v-if="validationErrors.source_rename" class="icon is-small has-text-danger">
                <octicon :icon="alert" />
              </span>
            </label>
            <div class="control">
              <input
                ref="nameInput"
                v-model="item.source_rename"
                class="input"
                type="text"
                :class="{ 'is-danger': validationErrors.source_rename }"
                :placeholder="$t('dataInputs.placeholders.name')"
                :readonly="!!item.id"
                :disabled="!!item.id"
                autocomplete="off"
                maxlength="30"
                @input="handleNameInput"
                @blur="handleNameBlur"
              >
            </div>
            <p v-if="validationErrors.source_rename" class="help is-danger">
              {{ validationErrors.source_rename }}
            </p>
          </div>

          <div class="field">
            <label class="label">
              {{ $t('description') }}
              <span v-if="validationErrors.description" class="icon is-small has-text-danger">
                <octicon :icon="alert" />
              </span>
            </label>
            <div class="control">
              <textarea
                v-model="item.description"
                class="textarea"
                :class="{ 'is-danger': validationErrors.description }"
                autocomplete="off"
                @input="handleDescriptionInput"
                rows="3"
                maxlength="4096"
                :placeholder="$t('dataInputs.placeholders.description')"
              ></textarea>
            </div>
            <p v-if="validationErrors.description" class="help is-danger">
              {{ validationErrors.description }}
            </p>
            <p v-if="descriptionCharacterCount > 0" class="help" :class="{ 'has-text-danger': descriptionCharacterCount > maxDescriptionLength }">
              {{ descriptionCharacterCount }}/{{ maxDescriptionLength }} {{ $t('characters') }}
            </p>
          </div>

          <div class="field">
            <label class="label">{{ $t('protocol') }} *</label>
            <div class="control">
              <div class="custom-select-wrapper custom-select-inline" :class="{ 'is-open': isProtocolDropdownOpen }">
                <span class="select">
                  <select 
                    ref="protocolSelect"
                    v-model="item.protocol"
                    @mousedown="handleProtocolMousedown"
                    @blur="isProtocolDropdownOpen = false"
                    @change="isProtocolDropdownOpen = false"
                  >
                    <option v-for="protocol in protocols" :key="protocol" :value="protocol">{{ protocol.toUpperCase() }}</option>
                  </select>
                </span>
                <span class="dropdown-arrow" :class="{ 'is-rotated': isProtocolDropdownOpen }" @mousedown.prevent="toggleProtocolDropdown">
                  <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M2 4L6 8L10 4" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                </span>
              </div>
            </div>
          </div>

          <div v-if="item.protocol === 'tcp' || item.protocol === 'udp'" class="field">
            <label class="label">
              {{ $t('dataInputs.labels.port') }} *
              <popover placement="top" width="300px">
                <span slot="trigger" class="icon is-small has-text-info" style="cursor: help;">
                  <octicon :icon="info" />
                </span>
                <div class="has-text-left is-size-7">
                  {{ $t('dataInputs.tooltips.port') }}
                </div>
              </popover>
              <span v-if="validationErrors.port" class="icon is-small has-text-danger">
                <octicon :icon="alert" />
              </span>
            </label>
            <div class="control" :class="{ 'is-loading': isValidatingPort }">
              <input
                ref="portInput"
                v-model="item.port"
                class="input"
                type="text"
                :class="{ 'is-danger': validationErrors.port }"
                :placeholder="$t('dataInputs.placeholders.port')"
                autocomplete="off"
                @input="handlePortInput"
                @paste="handlePortPaste"
                @blur="handlePortBlur"
              >
            </div>
            <p v-if="validationErrors.port" class="help is-danger">
              {{ validationErrors.port }}
            </p>
          </div>

          <div v-if="item.protocol === 'http'" class="field">
            <label class="label">
              {{ $t('dataInputs.labels.token') }} *
              <popover placement="top" width="300px">
                <span slot="trigger" class="icon is-small has-text-info" style="cursor: help;">
                  <octicon :icon="info" />
                </span>
                <div class="has-text-left is-size-7">
                  {{ $t('dataInputs.tooltips.tokenCode') }}
                </div>
              </popover>
              <span v-if="validationErrors.token" class="icon is-small has-text-danger">
                <octicon :icon="alert" />
              </span>
            </label>
            <div class="field has-addons">
              <p class="control is-expanded">
                <input
                  v-model="item.token"
                  class="input"
                  type="text"
                  :placeholder="$t('token')"
                  autocomplete="off"
                  readonly
                  :class="{ 'is-danger': validationErrors.token }"
                >
              </p>
              <div class="control">
                <popover placement="top" width="200px">
                  <button slot="trigger" type="button" class="button" @click.prevent="randomHTTPToken" :title="$t('dataInputs.tooltips.generateToken')" :disabled="!!item.id">
                    <octicon :icon="sync" />
                  </button>
                  <div class="has-text-left is-size-7">
                    {{ $t('dataInputs.tooltips.generateToken') }}
                  </div>
                </popover>
              </div>
              <div class="control">
                <popover placement="top" width="200px">
                  <button slot="trigger" type="button" class="button" @click.prevent="copyHTTPToken" :title="$t('dataInputs.tooltips.copyToken')">
                    <octicon :icon="clippy" />
                  </button>
                  <div class="has-text-left is-size-7">
                    {{ $t('dataInputs.tooltips.copyToken') }}
                  </div>
                </popover>
              </div>
            </div>
            <p v-if="validationErrors.token" class="help is-danger">
              {{ validationErrors.token }}
            </p>
          </div>

          <div class="field">
            <label class="label">
              SSL
              <popover placement="top" width="300px">
                <span slot="trigger" class="icon is-small has-text-info" style="cursor: help;">
                  <span>(?)</span>
                </span>
                <div class="has-text-left is-size-7">
                  {{ $t('dataInputs.tooltips.ssl') }}
                </div>
              </popover>
            </label>
            <input
              id="ssl" v-model="item.ssl" type="checkbox"
              name="ssl" class="switch"
              :disabled="item.protocol !== 'tcp'"
            >
            <label for="ssl">{{ item.ssl ? $t('enabled') : $t('disabled') }}</label>
          </div>

          <div class="field" @keydown.enter.prevent>
            <label class="label">
              {{ $t('dataInputs.labels.accessIPs') }}
              <popover placement="top" width="300px">
                <span slot="trigger" class="icon is-small has-text-info" style="cursor: help;">
                  <span>(?)</span>
                </span>
                <div class="has-text-left is-size-7">
                  {{ $t('dataInputs.tooltips.allowedIPs') }}
                </div>
              </popover>
              <span v-if="validationErrors.access_ips" class="icon is-small has-text-danger">
                <octicon :icon="alert" />
              </span>
            </label>
            <div class="control">
              <div class="tags-input" :class="{ 'is-danger': validationErrors.access_ips, 'is-disabled': item.protocol === 'http' }">
                <span v-for="(ip, index) in item.access_ips" :key="index" class="tag is-primary">
                  {{ ip }}
                  <button type="button" class="delete is-small" @click="removeIP(index)" :disabled="item.protocol === 'http'"></button>
                </span>
                <input
                  v-model="currentIPInput"
                  type="text"
                  class="tags-input-field"
                  :placeholder="(item.access_ips || []).length === 0 ? $t('dataInputs.placeholders.accessIPs') : ''"
                  :disabled="item.protocol === 'http'"
                  autocomplete="off"
                  @input="handleIPInputChange"
                  @keydown.enter.prevent="addIP"
                  @blur="validateAllowedIPsField"
                  maxlength="16"
                >
              </div>
            </div>
            <p v-if="validationErrors.access_ips" class="help is-danger">
              {{ validationErrors.access_ips }}
            </p>
          </div>

          <div class="columns">
            <div class="column">
              <label class="label">
                {{ $t('index') }} *
                <span v-if="validationErrors.index" class="icon is-small has-text-danger">
                  <octicon :icon="alert" />
                </span>
              </label>
              <div class="field">
                <div class="custom-select-wrapper" :class="{ 'is-open': isIndexDropdownOpen }">
                  <div class="select is-fullwidth">
                    <select 
                      ref="indexSelect"
                      v-model="item.index" 
                      :class="{ 'is-danger': validationErrors.index }" 
                      @change="clearFieldError('index'); isIndexDropdownOpen = false"
                      @mousedown="handleIndexMousedown"
                      @blur="isIndexDropdownOpen = false"
                    >
                      <option value="" disabled>{{ $t('selectDefault') }}</option>
                      <option
                        v-for="index in activeIndexes"
                        :key="index.id"
                        :value="index.id"
                      >
                        {{ index.name }}
                      </option>
                    </select>
                  </div>
                  <span class="dropdown-arrow" :class="{ 'is-rotated': isIndexDropdownOpen }" @mousedown.prevent="toggleIndexDropdown">
                    <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M2 4L6 8L10 4" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                    </svg>
                  </span>
                </div>
                <p v-if="validationErrors.index" class="help is-danger">
                  {{ validationErrors.index }}
                </p>
              </div>
            </div>
            <div class="column">
              <label class="label">
                {{ $t('sourceType') }} *
                <span v-if="validationErrors.source_type" class="icon is-small has-text-danger">
                  <octicon :icon="alert" />
                </span>
              </label>
              <div class="field">
                <div class="custom-select-wrapper" :class="{ 'is-open': isSourceTypeDropdownOpen }">
                  <div class="select is-fullwidth">
                    <select 
                      ref="sourceTypeSelect"
                      v-model="item.source_type" 
                      :class="{ 'is-danger': validationErrors.source_type }" 
                      @change="clearFieldError('source_type'); isSourceTypeDropdownOpen = false"
                      @mousedown="handleSourceTypeMousedown"
                      @blur="isSourceTypeDropdownOpen = false"
                    >
                      <option value="" disabled>{{ $t('selectDefault') }}</option>
                      <option
                        v-for="sourceType in activeSourceTypes"
                        :key="sourceType.id"
                        :value="sourceType.id"
                      >
                        {{ sourceType.name }}
                      </option>
                    </select>
                  </div>
                  <span class="dropdown-arrow" :class="{ 'is-rotated': isSourceTypeDropdownOpen }" @mousedown.prevent="toggleSourceTypeDropdown">
                    <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M2 4L6 8L10 4" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                    </svg>
                  </span>
                </div>
                <p v-if="validationErrors.source_type" class="help is-danger">
                  {{ validationErrors.source_type }}
                </p>
              </div>
            </div>
          </div>

          <div class="modal-footer">
            <button type="button" class="button cancel-button" @click.prevent="closeModal" :disabled="isValidating">
              {{ $t('cancel') }}
            </button>
            <button
              type="submit"
              class="button is-primary"
              :class="{ 'is-loading': isValidating }"
              :disabled="isValidating"
            >
              {{ $t('save') }}
            </button>
          </div>
        </form>
      </div>
    </modal>

    <!-- Data Input Filter Modal -->
    <data-input-filter
      :show.sync="showFilter"
      :filters="currentFilters"
      @apply-filters="applyFilters"
    />

    <!-- Data Input Delete Confirmation Dialog -->
    <confirmation-dialog
      :show="showDeleteConfirmation"
      :title="$t('dataInputs.deleteConfirmation.title')"
      :message="deleteConfirmationMessage"
      :warning-text="$t('dataInputs.deleteConfirmation.warning')"
      :cancel-text="$t('dataInputs.deleteConfirmation.cancel')"
      :confirm-text="$t('dataInputs.deleteConfirmation.confirm')"
      confirm-button-class="is-danger"
      :show-countdown="true"
      :countdown-seconds="5"
      :countdown-message-template="$t('dataInputs.deleteConfirmation.countdown')"
      @confirm="handleDeleteConfirm"
      @cancel="handleDeleteCancel"
    />

    <!-- Data Input Status Confirmation Dialog -->
    <confirmation-dialog
      :show="showStatusConfirmation"
      :title="statusConfirmationTitle"
      :message="statusConfirmationMessage"
      warning-text=""
      :cancel-text="$t('dataInputs.statusConfirmation.cancel')"
      :confirm-text="$t('dataInputs.statusConfirmation.confirm')"
      :confirm-button-class="isActivatingStatus ? 'is-success' : 'is-warning'"
      :show-countdown="false"
      @confirm="handleStatusConfirm"
      @cancel="handleStatusCancel"
    />
  </section>
</template>
<script>
import DataTable from '@/components/common/DataTable.vue'
import { Modal, Popover } from '@cyradar/ui'
import { plus, x, pencil, sync, clippy, play, circleSlash, search, info, alert } from 'octicons-vue'
import { v4 as uuid } from 'uuid'
import clipboard from '@/clipboard.js'

import DataInputFilter from '@/components/data-inputs/DataInputFilter.vue'
import ConfirmationDialog from '@/components/common/ConfirmationDialog.vue'
import {
  validateDataInputForm,
  validateDataSourceName,
  validateDataSourceDescription,
  validatePort,
  validateAllowedIPs,
  validateIPAddress,
  applyInputFilter
} from '@/utils/dataInputValidation.js'

const protocols = ['http', 'tcp', 'udp']
const nameMap = (names) => names.reduce((obj, item) => {
  obj[item.id] = item
  return obj
}, {})

export default {
  components: { DataTable, Modal, DataInputFilter, ConfirmationDialog, Popover },
  data () {
    return {
      modal: false,
      item: null,
      source_types: [],
      indexes: [],
      toggleLoading: {},
      showFilter: false,
      showDeleteConfirmation: false,
      itemToDelete: null,
      showStatusConfirmation: false,
      itemToToggle: null,
      isActivatingStatus: false,
      currentFilters: {
        enabled: [],
        ssl: [],
        protocol: []
      },
      actualFilteredCount: 0,
      validationErrors: {
        source_rename: '',
        description: '',
        port: '',
        access_ips: '',
        index: '',
        source_type: '',
        token: ''
      },
      isValidating: false,
      isValidatingPort: false,
      descriptionCharacterCount: 0,
      maxDescriptionLength: 4096,
      existingDataSources: [],
      currentIPInput: '',
      // Dropdown open states
      isProtocolDropdownOpen: false,
      isIndexDropdownOpen: false,
      isSourceTypeDropdownOpen: false,
      // New data properties for custom DataTable
      dataInputs: [],
      loading: false,
      totalItems: 0,
      currentPage: 1,
      currentPageSize: 10,
      currentSort: {
        by: 'created_at',
        order: 'desc'
      },
      currentSearch: ''
    }
  },
  computed: {
    plus () {
      return plus
    },
    x () {
      return x
    },
    pencil () {
      return pencil
    },
    sync () {
      return sync
    },
    clippy () {
      return clippy
    },
    play () {
      return play
    },
    circleSlash () {
      return circleSlash
    },
    search () {
      return search
    },
    info () {
      return info
    },
    alert () {
      return alert
    },
    protocols () {
      return protocols
    },
    /**
     * Check if any filters are currently active
     */
    hasActiveFilters() {
      return this.actualFilteredCount > 0
    },
    /**
     * Count of active filters for display - show the actual result count from filter modal
     */
    activeFilterCount() {
      return this.actualFilteredCount
    },
    // Delete confirmation message
    deleteConfirmationMessage() {
      const name = this.itemToDelete ? this.itemToDelete.source_rename : ''
      return this.$t('dataInputs.deleteConfirmation.message', { name })
    },
    // Status confirmation title
    statusConfirmationTitle() {
      const name = this.itemToToggle ? this.itemToToggle.source_rename : ''
      return this.isActivatingStatus 
        ? this.$t('dataInputs.statusConfirmation.activateTitle', { name }) 
        : this.$t('dataInputs.statusConfirmation.deactivateTitle', { name })
    },
    // Status confirmation message
    statusConfirmationMessage() {
      return this.isActivatingStatus 
        ? this.$t('dataInputs.statusConfirmation.activateMessage') 
        : this.$t('dataInputs.statusConfirmation.deactivateMessage')
    },
    sourceTypeNames () {
      return nameMap(this.source_types)
    },
    indexNames () {
      return nameMap(this.indexes)
    },
    /**
     * Define columns for DataTable
     */
    columns() {
      return [
        {
          key: 'stt',
          label: this.$t('stt'),
          sortable: false,
          width: '60px',
          align: 'center'
        },
        {
          key: 'source_rename',
          label: this.$t('name'),
          field: 'source_rename',
          sortable: true,
          width: '150px',
          truncate: true
        },
        {
          key: 'description',
          label: this.$t('description'),
          field: 'description',
          sortable: true,
          width: '150px',
          truncate: true
        },
        {
          key: 'source_type',
          label: this.$t('sourceType'),
          field: 'source_type',
          sortable: true,
          width: '120px',
          truncate: true
        },
        {
          key: 'index',
          label: this.$t('index'),
          field: 'index',
          sortable: true,
          width: '120px',
          truncate: true
        },
        {
          key: 'enabled',
          label: this.$t('status.status'),
          field: 'enabled',
          sortable: true,
          width: '100px',
          align: 'center',
          defaultSortOrder: 'desc'
        },
        {
          key: 'ssl',
          label: 'SSL',
          field: 'ssl',
          sortable: true,
          width: '100px',
          align: 'center'
        },
        {
          key: 'protocol',
          label: this.$t('protocol'),
          field: 'protocol',
          sortable: true,
          width: '100px',
          align: 'center'
        },
        {
          key: 'port',
          label: this.$t('port'),
          field: 'port',
          sortable: true,
          width: '80px',
          align: 'center'
        },
        {
          key: 'created_at',
          label: this.$t('createdAt'),
          field: 'created_at',
          sortable: true,
          width: '150px',
          align: 'center'
        },
        {
          key: 'actions',
          label: this.$t('actions'),
          sortable: false,
          width: '120px',
          align: 'center'
        }
      ]
    },
    /**
     * Return available indexes
     */
    activeIndexes () {
      return this.indexes
    },
    /**
     * Filter source types to show only active ones
     */
    activeSourceTypes () {
      return this.source_types.filter(sourceType => sourceType.enabled !== false)
    },
    /**
     * Check if form is valid
     */
    isFormValid () {
      return !this.validationErrors.source_rename &&
             !this.validationErrors.description &&
             !this.validationErrors.port &&
             !this.validationErrors.access_ips &&
             !this.validationErrors.token &&
             this.item &&
             this.item.source_rename &&
             this.item.protocol &&
             this.item.source_type &&
             this.item.index &&
             (this.item.protocol !== 'http' || this.item.token)
    }
  },
  watch: {
    'item.protocol' (val, oldVal) {
      if (!val) {
        return
      }

      // Clear port when switching protocols
      if (oldVal) {
        this.item.port = ''
        this.validationErrors.port = ''
      }

      // Generate token for HTTP protocol if not already set
      if (val === 'http' && (!this.item.token || !this.item.id)) {
        this.randomHTTPToken()
      }
    },
    /**
     * Watch for filter changes and reload data
     */
    currentFilters: {
      handler() {
        this.fetchDataInputs()
      },
      deep: true
    }
  },
  mounted () {
    this.loadSourceTypes()
    this.loadIndexes()
    this.loadExistingDataSources()
    this.fetchDataInputs()

    // Prevent password manager popup by adding hidden dummy inputs
    this.$nextTick(() => {
      const searchInput = this.$el.querySelector('input[type="search"], input[placeholder*="Search"], input[placeholder*="Tìm kiếm"]')
      if (searchInput && searchInput.parentElement) {
        // Create hidden dummy inputs to mislead password managers
        const dummyUsername = document.createElement('input')
        dummyUsername.setAttribute('type', 'text')
        dummyUsername.setAttribute('name', 'fake_username_' + Math.random())
        dummyUsername.style.position = 'absolute'
        dummyUsername.style.top = '-9999px'
        dummyUsername.style.left = '-9999px'
        dummyUsername.setAttribute('tabindex', '-1')
        dummyUsername.setAttribute('aria-hidden', 'true')

        const dummyPassword = document.createElement('input')
        dummyPassword.setAttribute('type', 'password')
        dummyPassword.setAttribute('name', 'fake_password_' + Math.random())
        dummyPassword.style.position = 'absolute'
        dummyPassword.style.top = '-9999px'
        dummyPassword.style.left = '-9999px'
        dummyPassword.setAttribute('tabindex', '-1')
        dummyPassword.setAttribute('aria-hidden', 'true')

        // Insert dummy inputs before the search input
        searchInput.parentElement.insertBefore(dummyUsername, searchInput)
        searchInput.parentElement.insertBefore(dummyPassword, searchInput)
      }
    })
  },
  methods: {
    /**
     * Handles IP input change - filters in real-time to allow only numbers and dots
     */
    handleIPInputChange() {
      // Filter to only allow numbers and dots in real-time
      const filtered = applyInputFilter(this.currentIPInput, 'ip')
      this.currentIPInput = filtered.substring(0, 16)
      // Clear error when typing
      this.clearFieldError('access_ips')
    },
    /**
     * Adds IP to the list when Enter is pressed
     */
    addIP() {
      const trimmedIP = this.currentIPInput.trim()
      
      // Don't add empty IP
      if (!trimmedIP) {
        return
      }
      
      // Check if IP already exists
      if (this.item.access_ips.includes(trimmedIP)) {
        this.validationErrors.access_ips = this.$t('dataInputs.validation.ipDuplicate')
        return
      }
      
      // Validate IP format before adding
      const ipValidation = validateIPAddress(trimmedIP, (key, params) => this.$t(key, params))
      if (!ipValidation.isValid) {
        this.validationErrors.access_ips = ipValidation.error
        return
      }
      
      // IP is valid, add to array and clear input
      this.item.access_ips.push(trimmedIP)
      this.currentIPInput = ''
      this.clearFieldError('access_ips')
    },
    /**
     * Removes IP from the list
     */
    removeIP(index) {
      this.item.access_ips.splice(index, 1)
      this.clearFieldError('access_ips')
    },
    /**
     * Validates data source name and updates validation state
     */
    async validateName() {
      const validation = validateDataSourceName(
        this.item.source_rename,
        (key, params) => this.$t(key, params),
        this.existingDataSources,
        !!this.item.id
      )
      this.validationErrors.source_rename = validation.error
      return validation.isValid
    },

    /**
     * Validates description and updates character count
     */
    validateDescription() {
      const validation = validateDataSourceDescription(this.item.description, (key, params) => this.$t(key, params))
      this.validationErrors.description = validation.error
      this.descriptionCharacterCount = validation.characterCount
      this.maxDescriptionLength = validation.maxLength
      return validation.isValid
    },

    /**
     * Validates port field
     */
    async validatePortField() {
      if (this.item.protocol === 'tcp' || this.item.protocol === 'udp') {
        const validation = validatePort(this.item.port, (key, params) => this.$t(key, params))
        this.validationErrors.port = validation.error

        // Only update port value if correctedValue is valid (not NaN or empty string for invalid cases)
        if (validation.correctedValue !== undefined && 
            validation.correctedValue !== this.item.port && 
            !isNaN(validation.correctedValue) && 
            validation.correctedValue !== '') {
          this.item.port = validation.correctedValue
        }

        if (!validation.isValid) {
          return false
        }

        // Check if port already exists for this protocol
        try {
          const params = {
            port: this.item.port,
            protocol: this.item.protocol
          }

          // If editing, exclude current item from check
          if (this.item.id) {
            params.exclude_id = this.item.id
          }

          const response = await this.$http.get('/api/v1/data-inputs/check-port', { params })

          if (response && response.data && response.data.data && response.data.data.exists) {
            this.validationErrors.port = this.$t('dataInputs.validation.portExists') || 'Port already exists for this protocol'
            return false
          }
        } catch (error) {
          console.error('Error checking port existence:', error)
          // Don't block submission if API call fails
        }

        return validation.isValid
      } else {
        this.validationErrors.port = ''
        return true
      }
    },

    /**
     * Validates allowed IP addresses
     */
    validateAllowedIPsField() {
      const validation = validateAllowedIPs(this.item.access_ips, (key, params) => this.$t(key, params))
      this.validationErrors.access_ips = validation.error
      return validation.isValid
    },

    /**
     * Validates all form fields
     */
    async validateForm() {
      this.isValidating = true

      // Skip name validation in Update mode since name field is disabled
      const nameValid = this.item.id ? true : await this.validateName()
      const descriptionValid = this.validateDescription()
      const portValid = await this.validatePortField()
      const ipValid = this.validateAllowedIPsField()
      const indexValid = this.validateIndex()
      const sourceTypeValid = this.validateSourceType()
      const tokenValid = this.validateToken()

      this.isValidating = false

      return nameValid && descriptionValid && portValid && ipValid && indexValid && sourceTypeValid && tokenValid
    },

    /**
     * Validates index field
     */
    validateIndex() {
      if (!this.item.index) {
        this.validationErrors.index = this.$t('dataInputs.validation.indexRequired') || 'Index is required'
        return false
      }
      this.validationErrors.index = ''
      return true
    },

    /**
     * Validates source_type field
     */
    validateSourceType() {
      if (!this.item.source_type) {
        this.validationErrors.source_type = this.$t('dataInputs.validation.sourceTypeRequired') || 'Source Type is required'
        return false
      }
      this.validationErrors.source_type = ''
      return true
    },

    /**
     * Validates token field for HTTP protocol
     */
    validateToken() {
      if (this.item.protocol === 'http') {
        if (!this.item.token || this.item.token.trim() === '') {
          this.validationErrors.token = this.$t('dataInputs.validation.tokenRequired') || 'Token is required for HTTP protocol'
          return false
        }
      }
      this.validationErrors.token = ''
      return true
    },

    /**
     * Clears validation error for a specific field
     */
    clearFieldError(field) {
      this.validationErrors[field] = ''
    },

    /**
     * Handles name input events
     */
    handleNameInput() {
      this.clearFieldError('source_rename')
      if (this.item.source_rename && this.item.source_rename.length > 30) {
        this.item.source_rename = this.item.source_rename.substring(0, 30)
      }
    },

    /**
     * Handles name blur events
     */
    async handleNameBlur() {
      const isValid = await this.validateName()
      if (!isValid && this.$refs.nameInput) {
        this.$nextTick(() => {
          this.$refs.nameInput.focus()
        })
      }
    },

    /**
     * Handles description input events
     */
    handleDescriptionInput() {
      this.clearFieldError('description')
      this.validateDescription()
    },

    /**
     * Handles port input events with filtering
     */
    handlePortInput() {
      this.clearFieldError('port')
      // Apply numeric filtering
      this.item.port = applyInputFilter(this.item.port, 'port')

      // Auto-clamp port value to valid range (0-65535)
      if (this.item.port !== '' && this.item.port !== null) {
        const portNum = parseInt(this.item.port, 10)
        if (!isNaN(portNum)) {
          if (portNum < 0) {
            this.item.port = '0'
          } else if (portNum > 65535) {
            this.item.port = '65535'
          }
        }
      }

      this.validatePortField()
    },

    /**
     * Handles port paste events
     */
    handlePortPaste(event) {
      event.preventDefault()
      const pastedText = (event.clipboardData || window.clipboardData).getData('text')
      const filteredValue = applyInputFilter(pastedText, 'port')
      this.item.port = filteredValue

      // Auto-clamp port value to valid range (0-65535)
      if (this.item.port !== '' && this.item.port !== null) {
        const portNum = parseInt(this.item.port, 10)
        if (!isNaN(portNum)) {
          if (portNum < 0) {
            this.item.port = '0'
          } else if (portNum > 65535) {
            this.item.port = '65535'
          }
        }
      }
    },

    /**
     * Handles port blur events
     */
    async handlePortBlur() {
      this.isValidatingPort = true
      try {
        await this.validatePortField()
      } finally {
        this.isValidatingPort = false
      }
    },

    /**
     * Handles IP input filtering
     */
    handleIPInput(value, index) {
      const filteredValue = applyInputFilter(value, 'ip')
      this.$set(this.item.access_ips, index, filteredValue)
      this.validateAllowedIPsField()
    },

    /**
     * Handles IP paste events
     */
    handleIPPaste(event, index) {
      event.preventDefault()
      const pastedText = (event.clipboardData || window.clipboardData).getData('text')
      const filteredValue = applyInputFilter(pastedText, 'ip')
      this.$set(this.item.access_ips, index, filteredValue)
      this.validateAllowedIPsField()
    },

    /**
     * Loads existing data sources for uniqueness validation
     */
    loadExistingDataSources() {
      return this.$http.get('/api/v1/data-inputs').then(body => {
        if (body && body.data && body.data.data && body.data.data.items) {
          this.existingDataSources = body.data.data.items
        }
      }).catch(() => {
        this.existingDataSources = []
      })
    },
    copyHTTPToken () {
      clipboard(this.item.token || '')
    },
    randomHTTPToken () {
      const token = uuid()
      if (!token) {
        return
      }

      this.$set(this.item, 'token', token.replace(/-/g, ''))
    },
    loadSourceTypes () {
      return this.$http.get('/api/v1/source-types/names').then(body => {
        if (!body || !body.data || !body.data.data) {
          return
        }

        this.source_types = body.data.data
      })
    },
    loadIndexes () {
      return this.$http.get('/api/v1/indexes/names').then(body => {
        if (!body || !body.data || !body.data.data) {
          return
        }

        this.indexes = body.data.data
      })
    },
    closeModal () {
      this.modal = false
      // Reset validation errors when closing modal
      this.validationErrors = {
        source_rename: '',
        description: '',
        port: '',
        access_ips: '',
        index: '',
        source_type: '',
        token: ''
      }
      this.descriptionCharacterCount = 0
    },
    async submit () {
      // Validate form before submission
      const isValid = await this.validateForm()

      if (!isValid) {
        this.$store.dispatch('NOTIFY', {
          type: 'error',
          text: this.$t('dataInputs.validation.formInvalid') || 'Please fix validation errors before submitting'
        })

        // Focus on the first error field
        this.focusFirstErrorField()
        return
      }

      if (this.item.id) {
        this.update()
        return
      }

      this.store()
    },
    /**
     * Focus on the first field with validation error
     */
    focusFirstErrorField() {
      this.$nextTick(() => {
        // Priority order: name, description, port, access_ips, index, source_type, token
        if (this.validationErrors.source_rename && this.$refs.nameInput) {
          this.$refs.nameInput.focus()
          this.$refs.nameInput.scrollIntoView({ behavior: 'smooth', block: 'center' })
          return
        }
        
        if (this.validationErrors.port && this.$refs.portInput) {
          this.$refs.portInput.focus()
          this.$refs.portInput.scrollIntoView({ behavior: 'smooth', block: 'center' })
          return
        }
        
        // Find the first input/textarea with is-danger class
        const errorElement = this.$el.querySelector('.is-danger')
        if (errorElement) {
          errorElement.focus()
          // Scroll the element into view if needed
          errorElement.scrollIntoView({ behavior: 'smooth', block: 'center' })
        }
      })
    },
    create () {
      this.item = {
        protocol: 'http',
        source_type: '',
        index: '',
        enabled: true,
        source_rename: '',
        description: '',
        port: '',
        access_ips: [],
        ssl: false,
        token: ''
      }

      // Generate token immediately for HTTP protocol
      this.randomHTTPToken()

      // Reset validation state
      this.validationErrors = {
        source_rename: '',
        description: '',
        port: '',
        access_ips: '',
        index: '',
        source_type: '',
        token: ''
      }
      this.descriptionCharacterCount = 0

      this.modal = true
    },
    edit (item) {
      this.item = JSON.parse(JSON.stringify(item))

      // Ensure access_ips is always an array
      if (!this.item.access_ips) {
        this.item.access_ips = []
      }

      // Reset validation state
      this.validationErrors = {
        source_rename: '',
        description: '',
        port: '',
        access_ips: '',
        index: '',
        source_type: '',
        token: ''
      }

      // Initialize character count for description
      this.validateDescription()

      this.modal = true
    },
    /**
     * Fetches data inputs from API with pagination, sorting, search, and filters
     */
    async fetchDataInputs() {
      this.loading = true
      try {
        const params = {
          page: this.currentPage,
          limit: this.currentPageSize,
        }

        if (this.currentSort.by !== '') {
          params.sort = this.currentSort.by
        }
        if (this.currentSort.order !== '') {
          params.order = this.currentSort.order === 'asc' ? 'asc' : 'desc'
        }

        // Add search filter if present
        if (this.currentSearch) {
          params.filter = this.currentSearch
        }

        // Add filter parameters if active
        if (this.currentFilters.enabled.length > 0) {
          params.enabled = this.currentFilters.enabled.join(',')
        }
        if (this.currentFilters.ssl.length > 0) {
          params.ssl = this.currentFilters.ssl.join(',')
        }
        if (this.currentFilters.protocol.length > 0) {
          params.protocol = this.currentFilters.protocol.join(',')
        }

        const response = await this.$http.get('/api/v1/data-inputs', { params })

        // Only update data after successful fetch to prevent flicker
        if (response && response.data && response.data.data) {
          this.dataInputs = response.data.data.items || []
          this.totalItems = response.data.data.total || 0
        }
      } catch (error) {
        console.error('Error fetching data inputs:', error)
        this.$store.dispatch('NOTIFY', {
          type: 'error',
          text: error.response?.data?.message || this.$t('dataInputs.messages.fetchError')
        })
        // Don't clear data on error to prevent flicker
      } finally {
        this.loading = false
      }
    },
    /**
     * Handle search event from DataTable
     */
    handleSearch(searchQuery) {
      this.currentSearch = searchQuery
      this.currentPage = 1 // Reset to first page on search
      this.fetchDataInputs()
    },
    /**
     * Handle sort event from DataTable
     */
    handleSort(sortConfig) {
      this.currentSort = {
        by: sortConfig.by || 'source_rename',
        order: sortConfig.order || 'asc'
      }
      this.currentPage = 1 // Reset to first page on sort
      this.fetchDataInputs()
    },
    /**
     * Handle page change event from DataTable
     */
    handlePageChange(page) {
      this.currentPage = page
      this.fetchDataInputs()
    },
    /**
     * Handle page size change event from DataTable
     */
    handlePageSizeChange(pageSize) {
      this.currentPageSize = pageSize
      this.currentPage = 1 // Reset to first page on page size change
      this.fetchDataInputs()
    },
    store () {
      this.item.port = parseInt(this.item.port)
      return this.$http.post('/api/v1/data-inputs', this.item).then(body => {
        if (!body || !body.data) {
          return
        }

        this.closeModal()
        this.$store.dispatch('NOTIFY', {
          type: 'success',
          text: this.$t('dataInputs.messages.createSuccess')
        })

        this.fetchDataInputs()
      }).catch(error => {
        this.$store.dispatch('NOTIFY', {
          type: 'error',
          text: error.response?.data?.message || this.$t('dataInputs.messages.createError')
        })
      })
    },
    update () {
      this.item.port = parseInt(this.item.port)
      return this.$http.patch(`/api/v1/data-inputs/${this.item.id}`, this.item).then(body => {
        if (!body || !body.data) {
          return
        }

        this.closeModal()
        this.$store.dispatch('NOTIFY', {
          type: 'success',
          text: this.$t('dataInputs.messages.updateSuccess')
        })

        this.fetchDataInputs()
      }).catch(error => {
        this.$store.dispatch('NOTIFY', {
          type: 'error',
          text: error.response?.data?.message || this.$t('dataInputs.messages.updateError')
        })
      })
    },
    destroy (item) {
      // Store the item to delete and show confirmation dialog
      this.itemToDelete = item
      this.showDeleteConfirmation = true
    },

    /**
     * Handles the confirmation of data input deletion
     */
    handleDeleteConfirm() {
      if (!this.itemToDelete) {
        return
      }

      return this.$http.delete(`/api/v1/data-inputs/${this.itemToDelete.id}`).then(body => {
        if (!body || !body.data) {
          return
        }

        this.$store.dispatch('NOTIFY', {
          type: 'success',
          text: this.$t('dataInputs.messages.deleteSuccess')
        })

        this.fetchDataInputs()
      }).catch(error => {
        this.$store.dispatch('NOTIFY', {
          type: 'error',
          text: error.response?.data?.message || this.$t('dataInputs.messages.deleteError')
        })
      }).finally(() => {
        // Close dialog and clear the item to delete
        this.showDeleteConfirmation = false
        this.itemToDelete = null
      })
    },

    /**
     * Handles the cancellation of data input deletion
     */
    handleDeleteCancel() {
      this.showDeleteConfirmation = false
      this.itemToDelete = null
    },
    toggleActivation (item) {
      // Store the item and show confirmation dialog
      this.itemToToggle = item
      this.isActivatingStatus = !item.enabled
      this.showStatusConfirmation = true
    },

    /**
     * Handles the confirmation of status change
     */
    handleStatusConfirm() {
      if (!this.itemToToggle) {
        return
      }

      const item = this.itemToToggle
      const isActive = item.enabled
      const endpoint = isActive ? 'deactivate' : 'activate'
      const successKey = isActive ? 'dataInputs.messages.deactivateSuccess' : 'dataInputs.messages.activateSuccess'
      const errorKey = isActive ? 'dataInputs.messages.deactivateError' : 'dataInputs.messages.activateError'

      // Set loading state
      this.$set(this.toggleLoading, item.id, true)

      return this.$http.patch(`/api/v1/data-inputs/${item.id}/${endpoint}`).then(body => {
        if (!body || !body.data) {
          return
        }

        this.$store.dispatch('NOTIFY', {
          type: 'success',
          text: this.$t(successKey)
        })

        // Reload the table to reflect changes
        this.fetchDataInputs()
      }).catch(error => {
        this.$store.dispatch('NOTIFY', {
          type: 'error',
          text: error.response?.data?.message || this.$t(errorKey)
        })
      }).finally(() => {
        // Clear loading state
        this.$set(this.toggleLoading, item.id, false)
        // Close dialog and clear the item to toggle
        this.showStatusConfirmation = false
        this.itemToToggle = null
      })
    },

    /**
     * Handles the cancellation of status change
     */
    handleStatusCancel() {
      this.showStatusConfirmation = false
      this.itemToToggle = null
    },
    /**
     * Apply filters from the filter component
     */
    applyFilters(filters) {
      this.currentFilters = {
        enabled: [...(filters.enabled || [])],
        ssl: [...(filters.ssl || [])],
        protocol: [...(filters.protocol || [])]
      }

      // Use the result count from the filter modal
      this.actualFilteredCount = filters.resultCount || 0

      // Reset to first page when applying filters
      this.currentPage = 1

      // Reload the table with new filters
      this.fetchDataInputs()
    },
    /**
     * Format datetime for display
     */
    formatDateTime(value) {
      if (!value) return '-'

      // Check if it's the zero time from Go (0001-01-01T00:00:00Z)
      if (value === '0001-01-01T00:00:00Z' || value.startsWith('0001-01-01')) return '-'

      const date = new Date(value)
      if (isNaN(date.getTime())) return '-'

      // Check if year is 0001 (zero time)
      if (date.getFullYear() === 1) return '-'

      const year = date.getFullYear()
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')
      const hours = String(date.getHours()).padStart(2, '0')
      const minutes = String(date.getMinutes()).padStart(2, '0')
      const seconds = String(date.getSeconds()).padStart(2, '0')

      return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`
    },
    /**
     * Highlight search text in cell value
     */
    highlightSearchText(text) {
      if (!this.currentSearch || !text) {
        return text
      }

      const textStr = String(text)
      const queryStr = String(this.currentSearch)
      const regex = new RegExp(`(${queryStr.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi')
      return textStr.replace(regex, '<strong>$1</strong>')
    },
    /**
     * Handle protocol select mousedown - toggle on second click
     */
    handleProtocolMousedown(event) {
      if (this.isProtocolDropdownOpen) {
        event.preventDefault()
        this.isProtocolDropdownOpen = false
        if (this.$refs.protocolSelect) {
          this.$refs.protocolSelect.blur()
        }
      } else {
        this.isProtocolDropdownOpen = true
      }
    },
    /**
     * Handle index select mousedown - toggle on second click
     */
    handleIndexMousedown(event) {
      if (this.isIndexDropdownOpen) {
        event.preventDefault()
        this.isIndexDropdownOpen = false
        if (this.$refs.indexSelect) {
          this.$refs.indexSelect.blur()
        }
      } else {
        this.isIndexDropdownOpen = true
      }
    },
    /**
     * Handle source type select mousedown - toggle on second click
     */
    handleSourceTypeMousedown(event) {
      if (this.isSourceTypeDropdownOpen) {
        event.preventDefault()
        this.isSourceTypeDropdownOpen = false
        if (this.$refs.sourceTypeSelect) {
          this.$refs.sourceTypeSelect.blur()
        }
      } else {
        this.isSourceTypeDropdownOpen = true
      }
    },
    /**
     * Toggle protocol dropdown when clicking on arrow icon
     */
    toggleProtocolDropdown() {
      if (this.isProtocolDropdownOpen) {
        this.isProtocolDropdownOpen = false
        if (this.$refs.protocolSelect) {
          this.$refs.protocolSelect.blur()
        }
      } else {
        this.isProtocolDropdownOpen = true
        if (this.$refs.protocolSelect) {
          this.$refs.protocolSelect.focus()
        }
      }
    },
    /**
     * Toggle index dropdown when clicking on arrow icon
     */
    toggleIndexDropdown() {
      if (this.isIndexDropdownOpen) {
        this.isIndexDropdownOpen = false
        if (this.$refs.indexSelect) {
          this.$refs.indexSelect.blur()
        }
      } else {
        this.isIndexDropdownOpen = true
        if (this.$refs.indexSelect) {
          this.$refs.indexSelect.focus()
        }
      }
    },
    /**
     * Toggle source type dropdown when clicking on arrow icon
     */
    toggleSourceTypeDropdown() {
      if (this.isSourceTypeDropdownOpen) {
        this.isSourceTypeDropdownOpen = false
        if (this.$refs.sourceTypeSelect) {
          this.$refs.sourceTypeSelect.blur()
        }
      } else {
        this.isSourceTypeDropdownOpen = true
        if (this.$refs.sourceTypeSelect) {
          this.$refs.sourceTypeSelect.focus()
        }
      }
    }
  }
}
</script>

<style scoped>
/* Custom select wrapper with rotating arrow */
.custom-select-wrapper {
  position: relative;
  display: inline-block;
  width: 100%;
}

.custom-select-wrapper .select {
  width: 100%;
}

.custom-select-wrapper .select select {
  padding-right: 2.5em;
}

.custom-select-wrapper .dropdown-arrow {
  position: absolute;
  right: 0.75em;
  top: 50%;
  transform: translateY(-50%);
  pointer-events: auto;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #4a4a4a;
  transition: transform 0.2s ease;
  z-index: 1;
  cursor: pointer;
}

.custom-select-wrapper .dropdown-arrow.is-rotated {
  transform: translateY(-50%) rotate(180deg);
}

.custom-select-wrapper .dropdown-arrow svg {
  display: block;
}

/* Hide default select arrow */
.custom-select-wrapper .select::after {
  display: none;
}

/* Inline arrow style for Protocol dropdown */
.custom-select-inline {
  display: inline-block;
  width: auto;
}

.custom-select-inline .select {
  display: inline-block;
  width: auto;
}

.custom-select-inline .select select {
  padding-right: 2.5em;
  padding-left: 0.75em;
  width: auto;
  appearance: none;
  -webkit-appearance: none;
  -moz-appearance: none;
  background-color: white;
  border: 1px solid #dbdbdb;
  border-radius: 4px;
  cursor: pointer;
}

.custom-select-inline .dropdown-arrow {
  right: 0.75em;
  pointer-events: none;
}

/* Disabled field styling */
.field:has(input:disabled),
.field:has(.vs--disabled) {
  opacity: 0.5;
  pointer-events: none;
}

/* Disabled switch styling */
input.switch:disabled + label {
  opacity: 0.5;
  cursor: not-allowed;
}

/* Disabled v-select styling */
.vs--disabled {
  opacity: 0.6;
  pointer-events: none;
}

/* Select with danger state */
.select select.is-danger {
  border-color: #f14668;
}

.select select.is-danger:focus {
  box-shadow: 0 0 0 0.125em rgba(241, 70, 104, 0.25);
}

/* Custom tags input styling - matching Port input style */
.tags-input {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.25rem;
  width: 100%;
  max-width: 100%;
  padding: 0;
  border: none;
  background-color: transparent;
  cursor: text;
}

.tags-input.is-disabled {
  opacity: 0.5;
  pointer-events: none;
  cursor: not-allowed;
}

.tags-input-field {
  flex: 1;
  min-width: 0;
  width: 100%;
  max-width: 100%;
  height: 2.5em;
  padding: calc(0.5em - 1px) calc(0.75em - 1px);
  border: 1px solid #dbdbdb;
  border-radius: 4px;
  font-size: 1rem;
  line-height: 1.5;
  background-color: white;
  color: #363636;
  box-shadow: inset 0 0.0625em 0.125em rgba(10, 10, 10, 0.05);
  transition: border-color 0.2s ease;
}

.tags-input-field:hover {
  border-color: #b5b5b5;
}

.tags-input-field:focus {
  border-color: #485fc7;
  box-shadow: 0 0 0 0.125em rgba(72, 95, 199, 0.25);
  outline: none;
}

.tags-input.is-danger .tags-input-field {
  border-color: #f14668;
}

.tags-input.is-danger .tags-input-field:focus {
  box-shadow: 0 0 0 0.125em rgba(241, 70, 104, 0.25);
}

.tags-input-field::placeholder {
  color: rgba(54, 54, 54, 0.3);
}

.tags-input-field:disabled {
  background-color: whitesmoke;
  border-color: whitesmoke;
  box-shadow: none;
  cursor: not-allowed;
}

.tags-input .tag {
  margin: 0;
  margin-bottom: 0.25rem;
}
</style>

<style lang="scss">
// Additional custom styles for Data Input modal (non-scoped for modal)
@import '@/assets/styles/dark-modal.scss';

.dark-modal {
  .switch {
    & + label {
      color: $modal-text;
    }
  }
}
</style>
